import { computed, type Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';

export interface OrdersSummary {
  total_orders: string;
  completed_orders: string;
  pending_orders: string;
  pending_paid_orders: string;
  pending_paid_cash_orders: string;
  in_progress_orders: string;
  rejected_orders: string;
  served_orders: string;
  cancelled_orders: string;
  terminated_orders: string;
  failled_orders: string;
  ready_orders: string;
  paid_orders: string;
  payment_pending_orders: string;
  refunded_orders: string;
  cash_orders: string;
  online_orders: string;
  completion_rate: string | null;
  payment_success_rate: string | null;
  today_orders: string;
  today_completed: string;
  week_orders: string;
  month_orders: string;
}

export interface DailyTrend {
  date: string;
  orderCount: number;
  revenue?: number;
}

export interface StatusDistribution {
  orderStatus: { status: string; count: number }[];
  paymentStatus: { status: string; count: number }[];
  paymentMethods: { method: string; count: number }[];
}

export interface OrdersStatsData {
  summary: OrdersSummary;
  dailyTrends: DailyTrend[];
  statusDistribution: StatusDistribution;
}

interface OrdersStatsResponse {
  data: {
    summary: OrdersSummary[];
    dailyTrends: DailyTrend[];
    statusDistribution: StatusDistribution;
  };
  message: string;
}

function n(v: string | null | undefined): number {
  return parseInt(v ?? '0', 10) || 0;
}

export function useOrdersStats(
  restauIds: Ref<string[]>,
  start: Ref<string>,
  end: Ref<string>,
) {
  const enabled = computed(() => restauIds.value.length > 0);

  const {
    data: raw,
    isPending,
    isError,
  } = useQuery({
    queryKey: computed(() => [
      'orders-stats',
      restauIds.value.join(','),
      start.value,
      end.value,
    ]),
    enabled,
    queryFn: async () => {
      const parts: string[] = [];
      restauIds.value.forEach((id) =>
        parts.push(`restauId=${encodeURIComponent(id)}`),
      );
      if (start.value) parts.push(`start=${encodeURIComponent(start.value)}`);
      if (end.value) parts.push(`end=${encodeURIComponent(end.value)}`);
      const res = await axiosInstance.get<OrdersStatsResponse>(
        `orders/stats?${parts.join('&')}`,
        { headers: { 'x-client-app': 'admin-back-office' } },
      );
      return res.data.data;
    },
    staleTime: 5 * 60_000,
  });

  // Normalise la réponse en un objet plat facile à consommer
  const data = computed((): OrdersStatsData | null => {
    if (!raw.value) return null;
    const s = raw.value.summary?.[0];
    if (!s) return null;
    return {
      summary: s,
      dailyTrends: raw.value.dailyTrends ?? [],
      statusDistribution: raw.value.statusDistribution ?? {
        orderStatus: [],
        paymentStatus: [],
        paymentMethods: [],
      },
    };
  });

  // KPIs calculés depuis summary
  const kpis = computed(() => {
    const s = data.value?.summary;
    if (!s) return [];
    const total = n(s.total_orders);
    const completed = n(s.completed_orders);
    const compRate = total > 0 ? Math.round((completed / total) * 100) : 0;
    const paid = n(s.paid_orders);
    const paidRate = total > 0 ? Math.round((paid / total) * 100) : 0;
    return [
      {
        icon: 'pi-shopping-cart',
        label: 'Total commandes',
        value: String(total),
        desc: `Aujourd'hui ${n(s.today_orders)} · Semaine ${n(s.week_orders)} · Mois ${n(s.month_orders)}`,
        accent: true,
      },
      {
        icon: 'pi-check-circle',
        label: 'Terminées',
        value: `${completed} (${compRate}%)`,
        desc: `Servies ${n(s.served_orders)} · Prêtes ${n(s.ready_orders)}`,
      },
      {
        icon: 'pi-clock',
        label: 'En cours',
        value: String(n(s.in_progress_orders)),
        desc: `En attente ${n(s.pending_orders)} · Annulées ${n(s.cancelled_orders)}`,
      },
      {
        icon: 'pi-times-circle',
        label: 'Rejetées',
        value: String(n(s.rejected_orders)),
        desc: `Échouées ${n(s.failled_orders)} · Remboursées ${n(s.refunded_orders)}`,
      },
      {
        icon: 'pi-credit-card',
        label: 'Payées',
        value: `${paid} (${paidRate}%)`,
        desc: `En attente paiement ${n(s.payment_pending_orders)}`,
      },
      {
        icon: 'pi-wallet',
        label: 'Mode de paiement',
        value: `${n(s.online_orders)} en ligne`,
        desc: `Espèces ${n(s.cash_orders)}`,
      },
    ];
  });

  // Bar chart : statuts de commandes
  const chartData = computed(() => {
    const s = data.value?.summary;
    if (!s) return { values: [] as number[], labels: [] as string[] };
    return {
      values: [
        n(s.completed_orders),
        n(s.in_progress_orders),
        n(s.pending_orders),
        n(s.rejected_orders),
        n(s.ready_orders),
        n(s.cancelled_orders),
      ],
      labels: [
        'Terminées',
        'En cours',
        'En attente',
        'Rejetées',
        'Prêtes',
        'Annulées',
      ],
    };
  });

  // Donut : répartition paiements
  const donutSlices = computed(() => {
    const dist = data.value?.statusDistribution?.paymentStatus ?? [];
    if (dist.length) {
      const total = dist.reduce((a, b) => a + b.count, 0) || 1;
      const colors: Record<string, string> = {
        success: '#10b981',
        pending: '#f59e0b',
        failed: '#ef4444',
      };
      return dist
        .map((d) => ({
          label: d.status,
          value: Math.round((d.count / total) * 100),
          color: colors[d.status] ?? '#94a3b8',
        }))
        .filter((d) => d.value > 0);
    }
    // Fallback depuis summary
    const s = data.value?.summary;
    if (!s) return [];
    const paid = n(s.paid_orders);
    const pend = n(s.payment_pending_orders);
    const failed = n(s.failled_orders);
    const total = paid + pend + failed || 1;
    return [
      {
        label: 'Payées',
        value: Math.round((paid / total) * 100),
        color: '#10b981',
      },
      {
        label: 'En attente',
        value: Math.round((pend / total) * 100),
        color: '#f59e0b',
      },
      {
        label: 'Échouées',
        value: Math.round((failed / total) * 100),
        color: '#ef4444',
      },
    ].filter((d) => d.value > 0);
  });

  // Daily trends pour un éventuel line chart
  const dailyTrends = computed(() => data.value?.dailyTrends ?? []);

  return {
    data,
    kpis,
    chartData,
    donutSlices,
    dailyTrends,
    isPending,
    isError,
  };
}
